import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const TIER_COPY = {
  full: "Full refund — you're cancelling 48+ hours ahead.",
  half: "50% of the hourly charge is refundable (24–48h notice). Your deposit comes back in full.",
  deposit_only: "Under 24h notice: the hourly charge is non-refundable, but your deposit is returned.",
  host_full: "The host is cancelling — you'll be refunded in full.",
  unpaid: "This booking hasn't been paid, so there's nothing to refund.",
};

export const CancelDialog = ({ booking, open, onOpenChange, onDone }) => {
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && booking) {
      setQuote(null);
      api.get(`/bookings/${booking.id}/cancel-quote`).then((r) => setQuote(r.data))
        .catch(() => setQuote({ cancellable: false, reason: "Couldn't load the cancellation policy." }));
    }
  }, [open, booking]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/cancel`);
      toast.success(data.refund_amount > 0 ? `Cancelled — $${data.refund_amount.toFixed(2)} refunded` : "Booking cancelled");
      onDone(booking.id, data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cancel-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight text-xl">Cancel booking</DialogTitle>
          <DialogDescription className="sr-only">Review your refund and confirm cancellation</DialogDescription>
        </DialogHeader>
        {!quote ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-neutral-400" /></div>
        ) : !quote.cancellable ? (
          <div className="text-sm text-neutral-600 flex items-start gap-2 py-2">
            <AlertCircle className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" /> {quote.reason}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-neutral-600">{TIER_COPY[quote.tier] || "Review your refund below."}</p>
            <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-4 flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Estimated refund</span>
              <span className="font-display font-black text-2xl" data-testid="cancel-refund-amount">${(quote.refund_amount || 0).toFixed(2)}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-dialog-back">Keep booking</Button>
              <Button onClick={confirm} disabled={busy} className="bg-[#FF4500] hover:bg-[#E63E00] text-white" data-testid="cancel-dialog-confirm">
                {busy ? "Cancelling…" : "Confirm cancel"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CancelDialog;
